import {setCityDirection} from '../i18n';
import { attachPlanResizeHandle } from "./PlanResize";
import { appendPlanBackground, animatePlanImage } from "./PlanVisuals";
import { editorPanels } from "./EditorPanels";
import { CityApi } from "../api/client";
import type {
  FloorPlanData,
  HouseData,
  PlanAssetData,
  PlanDesignComponent,
  StorefrontPlansResponse,
} from "../api/types";
import {
  appendPlanAssetLights,
  planAssetImageTransform,
  planAssetZIndex,
  runtimePlanAsset,
} from "./PlanAssetLights";

const INTERNAL_COORDS: Array<[number, number]> = [
  [0, 0],
  [1, 0],
  [0, 1],
  [-1, 0],
];

function clonePlan(plan: FloorPlanData): FloorPlanData {
  return {
    ...plan,
    design: {
      backgroundColor: plan.design?.backgroundColor,
      useBackgroundImage: plan.design?.useBackgroundImage,
      components: (plan.design?.components ?? []).map((item) => ({ ...item })),
    },
  };
}

export class PlanDesigner {
  private readonly root = document.createElement("div");
  private readonly planTabs = document.createElement("div");
  private readonly stage = document.createElement("div");
  private readonly catalog = document.createElement("div");
  private readonly assetLibrary = document.createElement("div");
  private readonly inspector = document.createElement("div");
  private readonly notice = document.createElement("div");
  private house: HouseData | null = null;
  private response: StorefrontPlansResponse | null = null;
  private selectedPlans: FloorPlanData[] = [];
  private selectedPlanId: number | null = null;
  private selectedComponentId: string | null = null;

  constructor(private readonly api: CityApi) {
    this.root.className = "city-modal city-plan-designer";setCityDirection(this.root);
    this.root.hidden = true;

    const panel = document.createElement("section");
    panel.className = "city-modal__panel city-plan-designer__panel";

    const header = document.createElement("header");
    header.className = "city-modal__header";
    const heading = document.createElement("div");
    heading.innerHTML =
      "<strong>طراحی ویترین و فضای داخلی</strong><small>پلان‌ها و اَسِت‌های دوبعدی</small>";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "city-modal__close";
    close.textContent = "×";
    close.addEventListener("click", () => this.close());
    header.append(heading, close);

    const body = document.createElement("div");
    body.className = "city-plan-designer__body";

    const left = document.createElement("aside");
    left.className = "city-plan-designer__sidebar";
    const catalogTitle = document.createElement("strong");
    catalogTitle.textContent = "پلان‌های قابل انتخاب";
    this.catalog.className = "city-plan-catalog";
    left.append(catalogTitle, this.catalog);

    const center = document.createElement("main");
    center.className = "city-plan-designer__center";
    this.planTabs.className = "city-plan-tabs";
    this.stage.className = "city-plan-stage";
    this.notice.className = "city-modal__notice";
    this.notice.hidden = true;
    center.append(this.planTabs, this.stage, this.notice);

    const right = document.createElement("aside");
    right.className = "city-plan-designer__sidebar";
    const assetTitle = document.createElement("strong");
    assetTitle.textContent = "اَسِت‌های پلان";
    this.assetLibrary.className = "city-plan-assets";
    this.inspector.className = "city-plan-inspector";
    right.append(assetTitle, this.assetLibrary, this.inspector);

    body.append(left, center, right);

    const footer = document.createElement("footer");
    footer.className = "city-modal__footer";
    const save = document.createElement("button");
    save.type = "button";
    save.className = "city-btn city-btn--primary";
    save.textContent = "ذخیره طراحی داخلی";
    save.addEventListener("click", () => void this.save(save));
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "city-btn";
    cancel.textContent = "بستن";
    cancel.addEventListener("click", () => this.close());
    footer.append(save, cancel);

    const paneNav = editorPanels(body, [{ key: "view", label: "نمایش", element: center }, { key: "assets", label: "استت‌ها و تنظیمات", element: right }, { key: "plans", label: "پلان‌ها", element: left }], "view");
    panel.append(header, paneNav, body, footer);
    this.root.appendChild(panel);
    document.body.appendChild(this.root);
  }

  async open(house: HouseData): Promise<void> {
    this.house = house;
    this.root.hidden = false;
    document.body.classList.add("city-modal-open");
    this.notice.hidden = true;
    this.stage.innerHTML = '<p class="city-empty">در حال بارگذاری پلان‌ها…</p>';
    try {
      this.response = await this.api.storefrontPlans(house.externalId);
      this.selectedPlans = this.response.plans.map(clonePlan);
      this.selectedPlanId = this.selectedPlans[0]?.id ?? null;
      this.selectedComponentId = null;
      this.render();
    } catch (error) {
      this.showError(error);
    }
  }

  close(): void {
    this.root.hidden = true;
    document.body.classList.remove("city-modal-open");
  }

  destroy(): void {
    this.root.remove();
  }

  private render(): void {
    this.renderCatalog();
    this.renderTabs();
    this.renderStage();
    this.renderAssets();
    this.renderInspector();
  }

  private renderCatalog(): void {
    this.catalog.replaceChildren();
    const catalog = this.response?.catalog ?? [];
    if (!catalog.length) {
      this.catalog.innerHTML =
        '<p class="city-empty">مدیر هنوز پلانی برای این شهر تعریف نکرده است.</p>';
      return;
    }

    for (const plan of catalog) {
      const selected = this.selectedPlans.some((item) => item.id === plan.id);
      const row = document.createElement("div");
      row.className = "city-plan-catalog__item";
      const text = document.createElement("span");
      text.innerHTML = `<strong>${plan.title}</strong><small>${plan.typeLabel}</small>`;
      const action = document.createElement("button");
      action.type = "button";
      action.className = "city-btn";
      action.textContent = selected ? "حذف" : "افزودن";
      action.addEventListener("click", () => {
        if (selected) this.removePlan(plan.id);
        else this.addPlan(plan);
      });
      row.append(text, action);
      this.catalog.appendChild(row);
    }
  }

  private addPlan(plan: FloorPlanData): void {
    if (plan.type === "facade" &&
      this.selectedPlans.some((item) => item.type === "facade")) {
      this.setNotice("فقط یک پلان ویترین قابل انتخاب است.", true);
      return;
    }
    if (plan.type === "entry" &&
      this.selectedPlans.some((item) => item.type === "entry")) {
      this.setNotice("فقط یک پلان ورودی قابل انتخاب است.", true);
      return;
    }

    const internals = this.selectedPlans.filter((item) => item.type !== "facade");
    if (plan.type !== "facade" && internals.length >= 4) {
      this.setNotice("حداکثر چهار فضای داخلی قابل انتخاب است.", true);
      return;
    }

    const next = clonePlan(plan);
    if (plan.type !== "facade") {
      const used = new Set(
        internals.map((item) => `${item.gridX},${item.gridY}`),
      );
      const coord = INTERNAL_COORDS.find(([x, y]) => !used.has(`${x},${y}`))
        ?? INTERNAL_COORDS[internals.length % INTERNAL_COORDS.length];
      next.gridX = coord[0];
      next.gridY = coord[1];
    }
    next.sortOrder = this.selectedPlans.length;
    next.design = { components: [] };
    this.selectedPlans.push(next);
    this.selectedPlanId = next.id;
    this.selectedComponentId = null;
    this.render();
  }

  private removePlan(planId: number): void {
    this.selectedPlans = this.selectedPlans.filter((item) => item.id !== planId);
    if (this.selectedPlanId === planId) {
      this.selectedPlanId = this.selectedPlans[0]?.id ?? null;
      this.selectedComponentId = null;
    }
    this.selectedPlans.forEach((item, index) => {
      item.sortOrder = index;
    });
    this.render();
  }

  private renderTabs(): void {
    this.planTabs.replaceChildren();
    for (const plan of this.selectedPlans) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = plan.id === this.selectedPlanId
        ? "is-active"
        : "";
      button.textContent = plan.title;
      button.addEventListener("click", () => {
        this.selectedPlanId = plan.id;
        this.selectedComponentId = null;
        this.renderStage();
        this.renderInspector();
      });
      this.planTabs.appendChild(button);
    }
  }

  private activePlan(): FloorPlanData | null {
    return this.selectedPlans.find((item) => item.id === this.selectedPlanId)
      ?? null;
  }

  private renderStage(): void {
    this.stage.replaceChildren();
    const plan = this.activePlan();
    if (!plan) {
      this.stage.innerHTML =
        '<p class="city-empty">یک پلان را از ستون سمت راست انتخاب کنید.</p>';
      return;
    }

    const frame = document.createElement("div");
    frame.className = "city-plan-stage__frame";
    frame.style.backgroundColor=plan.design?.backgroundColor||plan.backgroundColor||"#f1f5f9";
    appendPlanBackground(frame, plan, "city-plan-stage__background");
    const assets = new Map(
      (this.response?.planAssets ?? []).map((asset) => [asset.id, asset]),
    );
    for (const component of plan.design?.components ?? []) {
      const asset = assets.get(component.assetId);
      if (!asset?.imageUrl) continue;
      appendPlanAssetLights(frame, asset, component, 100);
      const img = document.createElement("img");
      img.className = component.id === this.selectedComponentId
        ? "city-plan-stage__asset is-selected"
        : "city-plan-stage__asset";
      img.src = asset.imageUrl;
      animatePlanImage(img, runtimePlanAsset(asset));
      img.alt = asset.title;
      img.style.left = `${component.x}%`;
      img.style.top = `${component.y}%`;
      img.style.transform = planAssetImageTransform(asset, component);
      img.style.transformOrigin = `${(runtimePlanAsset(asset).anchorX ?? 0.5) * 100}% ${(runtimePlanAsset(asset).anchorY ?? 0.5) * 100}%`;
      img.style.zIndex = String(planAssetZIndex(asset, component, 100));
      img.addEventListener("pointerdown", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.selectedComponentId = component.id;
        frame.querySelectorAll(".is-selected").forEach((node) => node.classList.remove("is-selected"));
        img.classList.add("is-selected");
        frame.querySelectorAll("[data-plan-resize]").forEach((node) => node.remove());
        this.renderInspector();

        const frameRect = frame.getBoundingClientRect();
        if (!frameRect.width || !frameRect.height) return;
        const pointerX = ((event.clientX - frameRect.left) / frameRect.width) * 100;
        const pointerY = ((event.clientY - frameRect.top) / frameRect.height) * 100;
        const offsetX = pointerX - component.x;
        const offsetY = pointerY - component.y;
        img.setPointerCapture(event.pointerId);
        img.classList.add("is-dragging");

        const move = (moveEvent: PointerEvent) => {
          const rect = frame.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const x = ((moveEvent.clientX - rect.left) / rect.width) * 100 - offsetX;
          const y = ((moveEvent.clientY - rect.top) / rect.height) * 100 - offsetY;
          component.x = Math.max(-100, Math.min(200, Number(x.toFixed(2))));
          component.y = Math.max(-100, Math.min(200, Number(y.toFixed(2))));
          img.style.left = `${component.x}%`;
          img.style.top = `${component.y}%`;
        };

        const finish = () => {
          img.classList.remove("is-dragging");
          img.removeEventListener("pointermove", move);
          img.removeEventListener("pointerup", finish);
          img.removeEventListener("pointercancel", finish);
          this.renderStage();
          this.renderInspector();
        };

        img.addEventListener("pointermove", move);
        img.addEventListener("pointerup", finish);
        img.addEventListener("pointercancel", finish);
      });
      img.addEventListener("click", (event) => event.stopPropagation());
      frame.appendChild(img);
      if (component.id === this.selectedComponentId) {
        attachPlanResizeHandle(frame, img, component,
          () => { img.style.transform = planAssetImageTransform(asset, component); this.renderInspector(); },
          () => { this.renderStage(); this.renderInspector(); });
      }
    }
    frame.addEventListener("click", () => {
      this.selectedComponentId = null;
      this.renderStage();
      this.renderInspector();
    });
    this.stage.appendChild(frame);
  }

  private renderAssets(): void {
    this.assetLibrary.replaceChildren();
    for (const asset of this.response?.planAssets ?? []) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "city-plan-asset";
      const img = document.createElement("img");
      img.src = asset.imageUrl;
      img.alt = "";
      const label = document.createElement("span");
      label.textContent = asset.title;
      button.append(img, label);
      button.addEventListener("click", () => this.addAsset(asset));
      this.assetLibrary.appendChild(button);
    }
    if (!(this.response?.planAssets.length)) {
      this.assetLibrary.innerHTML =
        '<p class="city-empty">اَسِت داخلی فعالی وجود ندارد.</p>';
    }
  }

  private addAsset(asset: PlanAssetData): void {
    const plan = this.activePlan();
    if (!plan) return;
    const components = plan.design?.components ?? [];
    if (components.length >= 100) {
      this.setNotice("حداکثر ۱۰۰ اَسِت روی هر پلان مجاز است.", true);
      return;
    }
    const runtime = runtimePlanAsset(asset);
    const component: PlanDesignComponent = {
      id: `plan-${Date.now().toString(36)}-${components.length}`,
      assetId: asset.id,
      x: 50,
      y: 50,
      scale: runtime.defaultScale ?? 1,
      rotation: runtime.defaultRotation ?? 0,
      layer: components.length,
      flipX: false,
    };
    plan.design = { ...plan.design, components: [...components, component] };
    this.selectedComponentId = component.id;
    this.renderStage();
    this.renderInspector();
  }

  private selectedComponent(): PlanDesignComponent | null {
    const plan = this.activePlan();
    return plan?.design?.components.find(
      (item) => item.id === this.selectedComponentId,
    ) ?? null;
  }

  private renderInspector(): void {
    this.inspector.replaceChildren();
    const active=this.activePlan();
    if(active){
      const label=document.createElement("label");label.textContent="رنگ زمینه";
      const color=document.createElement("input");color.type="color";color.value=active.design?.backgroundColor||active.backgroundColor||"#f1f5f9";
      color.oninput=()=>{active.design={...active.design,components:active.design?.components??[],backgroundColor:color.value};this.renderStage();};label.append(color);this.inspector.append(label);
      if(active.imageUrl){const imageLabel=document.createElement("label");const imageCheck=document.createElement("input");imageCheck.type="checkbox";imageCheck.checked=active.design?.useBackgroundImage!==false;imageCheck.onchange=()=>{active.design={...active.design,components:active.design?.components??[],useBackgroundImage:imageCheck.checked};this.renderStage();};imageLabel.append(imageCheck,document.createTextNode(" استفاده از تصویر زمینه"));this.inspector.append(imageLabel);}

    }
    const component = this.selectedComponent();
    if (!component) {
      const tip=document.createElement("p");tip.className="city-empty";tip.textContent="اَسِت را انتخاب کنید؛ برای تغییر اندازه، گوشهٔ آبی آن را بکشید.";this.inspector.append(tip);
      return;
    }

    const asset = this.response?.planAssets.find(
      (item) => item.id === component.assetId,
    );
    const title = document.createElement("strong");
    title.textContent = asset?.title ?? "اَسِت";
    const resizeHint = document.createElement("p");
    resizeHint.className = "city-plan-resize-hint";
    resizeHint.textContent = "گوشهٔ آبی را بکشید تا اندازه تغییر کند؛ با کلیدهای جهت هم می‌توانید اندازه را تنظیم کنید.";
    this.inspector.append(resizeHint);

    const number = (
      label: string,
      key: "x" | "y" | "scale" | "rotation" | "layer",
      min: number,
      max: number,
      step: number,
    ) => {
      const row = document.createElement("label");
      row.textContent = label;
      const input = document.createElement("input");
      input.type = "number";
      input.min = String(min);
      input.max = String(max);
      input.step = String(step);
      input.value = String(component[key]);
      input.addEventListener("change", () => {
        const value = Math.max(min, Math.min(max, Number(input.value)));
        component[key] = value;
        this.renderStage();
      });
      row.appendChild(input);
      return row;
    };

    const flip = document.createElement("label");
    flip.className = "city-check";
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = component.flipX;
    checkbox.addEventListener("change", () => {
      component.flipX = checkbox.checked;
      this.renderStage();
    });
    flip.append(checkbox, document.createTextNode(" قرینه افقی"));

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "city-btn city-btn--danger";
    remove.textContent = "حذف اَسِت";
    remove.addEventListener("click", () => {
      const plan = this.activePlan();
      if (!plan?.design) return;
      plan.design.components = plan.design.components.filter(
        (item) => item.id !== component.id,
      );
      this.selectedComponentId = null;
      this.renderStage();
      this.renderInspector();
    });

    this.inspector.append(
      title,
      number("X (%)", "x", -100, 200, 1),
      number("Y (%)", "y", -100, 200, 1),
      number("مقیاس", "scale", 0.1, 4, 0.1),
      number("چرخش", "rotation", -180, 180, 5),
      number("لایه", "layer", -100, 100, 1),
      flip,
      remove,
    );
  }

  private async save(button: HTMLButtonElement): Promise<void> {
    if (!this.house) return;
    button.disabled = true;
    button.textContent = "در حال ذخیره…";
    try {
      await this.api.saveStorefrontPlans(
        this.house.externalId,
        this.selectedPlans.map((plan) => ({
          planId: plan.id,
          gridX: plan.gridX,
          gridY: plan.gridY,
          design: { components: plan.design?.components ?? [], backgroundColor:plan.design?.backgroundColor, useBackgroundImage:plan.design?.useBackgroundImage },
        })),
      );
      this.setNotice("طراحی داخلی ذخیره شد.");
      button.disabled = false;
      button.textContent = "ذخیره طراحی داخلی";
    } catch (error) {
      button.disabled = false;
      button.textContent = "ذخیره طراحی داخلی";
      this.showError(error);
    }
  }

  private setNotice(message: string, error = false): void {
    this.notice.hidden = false;
    this.notice.className = error
      ? "city-modal__notice is-error"
      : "city-modal__notice";
    this.notice.textContent = message;
  }

  private showError(error: unknown): void {
    this.setNotice(
      error instanceof Error ? error.message : "عملیات ناموفق بود.",
      true,
    );
  }
}
